<script>
/**
 * BaseSelfFilterToggle — atalho "Modo eu" no header de uma tela.
 *
 * NÃO é um eixo de filtro novo: é um gatilho rápido para o filtro de
 * **pessoa** que a tela já tem. O estado ativo é DERIVADO do filtro pelo pai
 * (ex.: `responsavelIds === [selfId]` no Board de Tarefas,
 * `id_profissional === meuId` em CRM › Transcrições) e chega aqui como prop —
 * sem cópia local, para o menu "Filtros" e este botão nunca divergirem.
 *
 * Componente é presentational: só emite `toggle` com o próximo estado
 * desejado (Boolean); quem aplica o filtro é a view dona da tela.
 *
 * Vive em `src/components/` (e não num módulo) porque tem mais de um
 * consumidor. É registrado globalmente pelo `_globals.js` como
 * `BaseSelfFilterToggle` — as views NÃO o importam: o gate
 * `design-system:no-regression` reprova import/registro local de `_base-*`
 * dentro de `src/modules/**`. Nos testes quem o registra é o `setup.js`,
 * espelhando o `main.js`.
 *
 * Destaque (pulse) é prop, não `:class` de fora. O root deste template é o
 * `<v-tooltip>`, cujo wrapper tem `display: contents` (ver <style> abaixo):
 * class/style bindados na tag `<BaseSelfFilterToggle>` caem nesse wrapper
 * sem caixa (0x0), onde `filter`/`transform` da animação não desenham nada, e
 * a custom property nem chega ao botão (o Vuetify renderiza o ativador como
 * irmão do wrapper, não como filho). `highlight`/`highlightDuration` aplicam
 * `az-highlight-pulse` (src/design/helpers/_effects.sass) direto no botão.
 *
 * Consumidores:
 *  - `@modules/tarefas/views/board/view-task-board.vue`
 *  - `@modules/crm/views/transcricoes/views/view-crm-transcricoes-history.vue`
 *    (único que usa `highlight`, na ação "Minhas transcrições" do Ctrl/Cmd+K)
 */
export default {
  name: 'BaseSelfFilterToggle',

  props: {
    // Estado derivado do filtro da tela: a pessoa selecionada é o logado.
    active: {
      type: Boolean,
      default: false,
    },
    // Reload dos dados em voo — bloqueia o clique e mostra o spinner do
    // BaseButton (UX: sem flash e sem duplo disparo).
    loading: {
      type: Boolean,
      default: false,
    },
    // Sessão sem profissional resolvido: mantém o botão visível porém inerte,
    // com dica explicando (em vez de sumir e confundir).
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledHint: {
      type: String,
      default: 'Disponível apenas para usuários com profissional vinculado.',
    },
    label: {
      type: String,
      default: 'Modo eu',
    },
    // Substantivo do que a tela lista, usado no aria-label e no tooltip
    // ("as suas tarefas" / "as suas transcrições").
    itemsLabel: {
      type: String,
      default: 'tarefas',
    },
    // Liga o pulse `az-highlight-pulse` no botão (feedback de "a busca global
    // acabou de ativar este filtro"). Quem controla o tempo ligado é o pai.
    highlight: {
      type: Boolean,
      default: false,
    },
    // Sobrescreve `--az-pulse-duration` (default 1.2s em _effects.sass).
    // String CSS, ex.: '2500ms'. `null` mantém o default da classe.
    highlightDuration: {
      type: String,
      default: null,
    },
  },

  computed: {
    // Vue 2 mescla `style` do pai com o style estático/bindado do root do
    // BaseButton (o <button>), então a custom property chega ao elemento
    // animado sem mexer no BaseButton.
    highlightStyle() {
      if (!this.highlightDuration) return null
      return { '--az-pulse-duration': this.highlightDuration }
    },
    ariaLabel() {
      return this.active
        ? `Modo eu ativo: mostrando apenas as suas ${this.itemsLabel}. Clique para mostrar todas.`
        : `Modo eu: mostrar apenas as suas ${this.itemsLabel}.`
    },
    tooltipText() {
      if (this.disabled) return this.disabledHint
      return this.active
        ? `Mostrando apenas as suas ${this.itemsLabel}`
        : `Mostrar apenas as suas ${this.itemsLabel}`
    },
  },

  methods: {
    onClick() {
      if (this.disabled || this.loading) return
      this.$emit('toggle', !this.active)
    },
  },
}
</script>

<template>
  <v-tooltip bottom class="base-self-filter-toggle__tip">
    <template v-slot:activator="{ on, attrs }">
      <BaseButton
        :primary="active && !disabled"
        :bordered="!active || disabled"
        fitContent
        class="base-self-filter-toggle"
        :class="{ 'az-highlight-pulse': highlight }"
        :style="highlightStyle"
        :loading="loading"
        :disabled="disabled"
        :aria-pressed="active ? 'true' : 'false'"
        :aria-label="ariaLabel"
        v-bind="attrs"
        v-on="on"
        @click="onClick"
      >
        <icon-svg name="user-2" :size="18" color="currentColor" />
        {{ label }}
      </BaseButton>
    </template>
    <span>{{ tooltipText }}</span>
  </v-tooltip>
</template>

<style lang="sass">
// O wrapper que o v-tooltip cria ao redor do ativador mede 0x0 mas continua
// sendo item de flex: no header (que usa `gap`) ele somaria o gap duas vezes
// ao redor do botão. `display: contents` tira o wrapper da árvore de layout.
// Não pode ser `scoped`: o wrapper é renderizado pelo v-tooltip, fora do
// escopo deste SFC. A classe própria limita o alcance a este toggle.
.base-self-filter-toggle__tip
  display: contents
</style>
